<template>
    <div class="sub_contents">
        <div class="cont_center">
            <h1 class="title_page">이벤트</h1>

            <!-- 로딩 상태 표시 -->
            <div v-if="eventData.isLoading.value" class="loading_area">
                <p>이벤트 정보를 불러오는 중...</p>
            </div>

            <!-- 에러 상태 표시 -->
            <div v-else-if="eventData.isError.value" class="error_area">
                <p>이벤트 정보를 불러오는 중 오류가 발생했습니다.</p>
                <button @click="eventData.refetch()" class="btn_retry">다시 시도</button>
            </div>

            <!-- 이벤트 상세 정보 -->
            <table class="view_tb" v-else-if="eventDetail.name">
                <colgroup class="m_none">
                    <col style="width: 240px">
                    <col>
                </colgroup>
                <colgroup class="pc_none">
                    <col style="width: 135px">
                    <col>
                </colgroup>
                <tbody>
                    <tr>
                        <th>이벤트명</th>
                        <td>{{ eventDetail.name }}</td>
                    </tr>
                    <tr>
                        <th>이벤트 기간</th>
                        <td>{{ eventDetail.startDd }} ~ {{ eventDetail.endDd }}</td>
                    </tr>
                    <tr>
                        <td colspan="2" class="event_contents">
                            <div v-html="eventDetail.rmks"></div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- 데이터 없음 -->
            <div v-else class="no_data_area">
                <p>이벤트 정보가 없습니다.</p>
            </div>

            <!-- 댓글영역 - replyYn이 'Y'일 때만 표시, replyInputYn이 'Y'면 참여완료만 표시 -->
            <div v-if="replyYn === 'Y' && eventDetail.name">
                <div v-if="eventDetail.replyInputYn === 'Y'" class="participated_area">
                    <p class="participated_txt">이미 참여 완료하셨습니다.</p>
                </div>
                <div v-else class="comment_area">
                    <h3 class="title_comment">댓글을 달아주세요.</h3>

                    <div class="comment_input_area">
                        <div class="comment_writer">
                            <h3 class="title_input">작성자명</h3>
                            <InputText v-model="form.name" :placeholder="placeholderWriter" :disabled="isLoggedIn"
                                :class="{ 'auto_filled': isLoggedIn }" />
                            <p v-if="isLoggedIn" class="auto_fill_info">로그인된 사용자 정보로 자동 입력됩니다.</p>
                        </div>

                        <!-- 댓글 입력과 버튼을 같은 라인에 배치하는 래퍼 -->
                        <div class="input_button_wrapper">
                            <div class="comment_input">
                                <h3 class="title_input">댓글</h3>
                                <p class="text_input_num">
                                    {{ form.rmks.length }}/
                                    <span>{{ maxLength }}</span>
                                    byte
                                </p>
                                <InputText v-model="form.rmks" :placeholder="placeholderComment" />
                            </div>
                            <div class="btn_shop_area">
                                <button @click="handleSubmitInquiry" class="btn_type2">등록</button>
                            </div>
                        </div>
                    </div>

                    <!-- 댓글 리스트 - replyListYn이 'Y'일 때만 표시 -->
                    <ul class="comment_list" v-if="eventDetail.replyListYn === 'Y' && replyList.length > 0">
                        <li :key="item.uuid || index" v-for="(item, index) in replyList">
                            <div class="comment_header">
                                <div class="comment_writer_info">
                                    <strong class="comment_writer2">
                                        {{ item.userName }}
                                    </strong>
                                    <span class="comment_date">
                                        {{ formatDateTime(item.regDt) }}
                                    </span>
                                </div>
                            </div>

                            <div class="comment_content">
                                <div class="comment_txt">
                                    {{ item.rmks }}
                                </div>

                                <!-- 삭제 버튼 (내가 쓴 댓글에만 표시) -->
                                <button v-if="canDeleteComment(item)" @click="handleDeleteComment(item)"
                                    class="btn_delete" type="button">
                                    삭제
                                </button>
                            </div>
                        </li>
                    </ul>

                    <!-- 댓글 없음 - replyYn이 'Y'이지만 replyListYn이 'N'이거나 댓글이 없을 때 -->
                    <div v-else-if="replyYn === 'Y' && eventDetail.replyListYn === 'N'" class="no_reply_area">
                        <p>댓글 목록이 비공개 처리되었습니다.</p>
                    </div>

                    <!-- 댓글 없음 - replyListYn이 'Y'이지만 댓글이 없을 때 -->
                    <div v-else-if="replyYn === 'Y' && eventDetail.replyListYn === 'Y' && replyList.length === 0"
                        class="no_reply_area">
                        <p>아직 댓글이 없습니다. 첫 번째 댓글을 작성해보세요!</p>
                    </div>
                </div>
            </div>

            <div class="btn_write_area">
                <button type="button" class="btn_type2" @click="routerLink">목록 보기</button>
            </div>
        </div>
    </div>

    <!-- 팝업들... -->
    <AlertPopup v-model="alertRequiredShow" title="알림">
        <template #layer_content>
            <p class="alert_txt">
                모든 항목은 필수 항목입니다.<br>
                빠짐없이 기입해 주세요.
            </p>
        </template>
    </AlertPopup>

    <AlertPopup v-model="alertSuccessShow" title="알림">
        <template #layer_content>
            <p class="alert_txt">
                댓글이 성공적으로 제출되었습니다.
            </p>
            <div class="btn_layer_area">
                <button type="button" class="btn_type1 small" @click="alertSuccessShow = false">확인</button>
            </div>
        </template>
    </AlertPopup>

    <AlertPopup v-model="confirmSubmitShow" title="확인" :customClass="dynamicClass">
        <template #layer_content>
            <p class="alert_txt">
                입력하신 내용으로<br>
                저장하시겠습니까?
            </p>

            <div class="btn_layer_area">
                <button type="button" class="btn_type1 small" @click="handleConfirm">확인</button>
            </div>
        </template>
    </AlertPopup>

    <AlertPopup v-model="alertLoginRequiredShow" title="Alert">
        <template #layer_content>
            <p class="alert_txt">
                회원만 참여 가능한 이벤트 입니다. 로그인 해주세요.
            </p>
            <div class="btn_layer_area">
                <button type="button" class="btn_type1 small" @click="handleLogin">확인</button>
            </div>
        </template>
    </AlertPopup>

    <!-- 댓글 삭제 확인 팝업 -->
    <AlertPopup v-model="confirmDeleteShow" title="Confirm" :customClass="dynamicClass">
        <template #layer_content>
            <p class="alert_txt">
                댓글을 삭제하시겠습니까?<br>
                삭제된 댓글은 복구할 수 없습니다.
            </p>

            <div class="btn_layer_area">
                <button type="button" class="btn_type1 small" @click="confirmDeleteShow = false">취소</button>
                <button type="button" class="btn_type1 small" @click="handleConfirmDelete">삭제</button>
            </div>
        </template>
    </AlertPopup>

    <!-- 댓글 삭제 성공 팝업 -->
    <AlertPopup v-model="alertDeleteSuccessShow" title="Alert">
        <template #layer_content>
            <p class="alert_txt">
                댓글이 성공적으로 삭제되었습니다.
            </p>
        </template>
    </AlertPopup>
</template>

<script>
import InputText from '@/components/InputText';
import { postAxios, getAxios, deleteAxios } from '@/lib/AxiosCall';
import AlertPopup from '@/components/AlertPopup.vue';
import { getUserId, getUserName, isAuthenticated } from "@/lib/UserInfo";
import { useRouter, useRoute } from "vue-router";
import { getBranchUuid } from '@/lib/Config';
import { setPageMeta, setNotFoundMeta } from '@/lib/Seo';
import { ref, watch, onMounted, nextTick, computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { LOCAL_STORAGE_HVM_USERINFO } from '@/lib/Constant';

export default {
    components: {
        InputText,
        AlertPopup
    },
    data() {
        return {
            placeholderWriter: '작성자명을 입력하세요.',
            placeholderComment: '댓글을 입력하여 이벤트에 참여하세요.',
            maxLength: 200,
            alertRequiredShow: false,
            alertSuccessShow: false,
            confirmSubmitShow: false,
            alertLoginRequiredShow: false,
            confirmDeleteShow: false,
            alertDeleteSuccessShow: false,
            dynamicClass: 'confirm',
            selectedComment: null // 삭제할 댓글 정보 저장
        };
    },
    setup() {
        const router = useRouter();
        const eventDetail = ref({});
        const replyList = ref([]);
        const replyYn = sessionStorage.getItem('replyYn');

        // 로그인 상태 확인
        const isLoggedIn = ref(false);
        const loginInfo = ref(null);

        // hvm_user_info에서 userId 가져오기
        let userId = '';
        try {
            const storedLoginInfo = localStorage.getItem(LOCAL_STORAGE_HVM_USERINFO);
            if (storedLoginInfo) {
                const parsedLoginInfo = JSON.parse(storedLoginInfo);
                userId = parsedLoginInfo.userId || '';
            }
        } catch (e) {
            userId = '';
        }

        const form = ref({
            name: '',
            rmks: '',
            id: userId,
            eventUuid: sessionStorage.getItem("eventUuid")
        });

        // 로그인 정보 확인 및 작성자명 자동 설정
        const checkLoginAndSetName = () => {
            try {
                const storedLoginInfo = localStorage.getItem(LOCAL_STORAGE_HVM_USERINFO);

                if (storedLoginInfo) {
                    const parsedLoginInfo = JSON.parse(storedLoginInfo);
                    console.log("로그인 정보:", parsedLoginInfo);

                    if (parsedLoginInfo && parsedLoginInfo.userId) {
                        isLoggedIn.value = true;
                        loginInfo.value = parsedLoginInfo;

                        // getUserName()을 작성자명으로 설정
                        const userName = getUserName() || parsedLoginInfo.userName || parsedLoginInfo.name;
                        form.value.name = userName;
                        form.value.id = getUserId() || parsedLoginInfo.userId;

                        console.log("작성자명 자동 설정:", userName);
                        console.log("사용자 ID 설정:", form.value.id);
                    } else {
                        isLoggedIn.value = false;
                        loginInfo.value = null;
                        form.value.name = '';
                        console.log("로그인되지 않은 상태");
                    }
                } else {
                    isLoggedIn.value = false;
                    loginInfo.value = null;
                    form.value.name = '';
                    console.log("로그인 정보 없음");
                }
            } catch (error) {
                console.error("로그인 정보 파싱 오류:", error);
                isLoggedIn.value = false;
                loginInfo.value = null;
                form.value.name = '';
            }
        };

        // 초기 로그인 상태 확인
        checkLoginAndSetName();

        function routerLink() {
            router.push("/HFO_PR_001");
        }

        const route = useRoute();

        // initialize from route param (preferred) or fall back to sessionStorage
        const initialEventUuid = route.params.eventUuid || sessionStorage.getItem('eventUuid');

        const branchUuid = getBranchUuid() || sessionStorage.getItem('branchUuid') || null;

        const eventReqData = ref({
            uuid: initialEventUuid,
            userId: userId, // userId 추가
            branchUuid: branchUuid
        });

        const replyReqData = ref({
            eventUuid: initialEventUuid,
            branchUuid: branchUuid
        });

        console.log("이벤트 요청 데이터:", eventReqData.value);
        console.log("댓글 요청 데이터:", replyReqData.value);

        // 이벤트 상세 정보 조회
        const eventData = useQuery({
            queryKey: ['get-event-detail', eventReqData.value],
            queryFn: async () => {
                console.log("이벤트 상세 API 호출:", eventReqData.value);
                const result = await getAxios("/hvm/v1/promotion/event", eventReqData.value);
                console.log("이벤트 상세 API 응답:", result);
                return result;
            },
            enabled: true, // 자동 실행 활성화
            retry: 3,
            retryDelay: 1000,
        });

        // watch route param changes and update request data + refetch
        watch(
            () => route.params.eventUuid,
            (newUuid) => {
                console.log('PR_003: route eventUuid changed', newUuid);
                const uuid = newUuid || sessionStorage.getItem('eventUuid');
                eventReqData.value.uuid = uuid;
                replyReqData.value.eventUuid = uuid;
                // ensure branchUuid is present (in case config loaded later)
                eventReqData.value.branchUuid = getBranchUuid() || eventReqData.value.branchUuid || sessionStorage.getItem('branchUuid');
                replyReqData.value.branchUuid = getBranchUuid() || replyReqData.value.branchUuid || sessionStorage.getItem('branchUuid');
                // update form.eventUuid as well
                form.value.eventUuid = uuid;
                // refetch queries
                if (eventData && eventData.refetch) eventData.refetch();
                if (replyData && replyData.refetch) replyData.refetch();
            }
        );

        // 이벤트 데이터 watch
        watch(
            () => eventData.data.value,
            (newData) => {
                console.log("이벤트 데이터 watch 트리거:", newData);

                if (newData && newData.page && newData.page.list && newData.page.list.length > 0) {
                    const event = newData.page.list[0];
                    console.log("이벤트 상세 정보:", event);

                    eventDetail.value = {
                        name: event.name,
                        startDd: event.startDd,
                        endDd: event.endDd,
                        rmks: event.rmks,
                        uuid: event.uuid,
                        replyYn: event.replyYn,
                        replyListYn: event.replyListYn,
                        ingFlag: event.ingFlag,
                        replyInputYn: event.replyInputYn // 추가
                    };

                    setPageMeta({
                        title: event.name ? event.name + ' | 이벤트' : '이벤트',
                        description: event.rmks || (event.name + ' 이벤트 기간 ' + event.startDd + ' ~ ' + event.endDd),
                        path: '/HFO_PR_003/' + event.uuid,
                        type: 'article',
                    });
                    console.log("eventDetail 업데이트:", eventDetail.value);
                    console.log("replyYn from API:", event.replyYn);
                    console.log("replyListYn from API:", event.replyListYn);
                } else {
                    console.warn("이벤트 데이터가 없습니다:", newData);
                    eventDetail.value = {};
                    setNotFoundMeta("이벤트");
                }
            },
            { immediate: true } // 즉시 실행
        );

        // 댓글 데이터 조회 - replyListYn이 'Y'일 때만 실행
        const replyData = useQuery({
            queryKey: ['get-event-reply', replyReqData.value],
            queryFn: async () => {
                console.log("댓글 API 호출:", replyReqData.value);
                const result = await getAxios("/hvm/v1/promotion/reply", replyReqData.value);
                console.log("댓글 API 응답:", result);
                return result;
            },
            enabled: computed(() => {
                const shouldEnable = replyYn === 'Y' && eventDetail.value.replyListYn === 'Y';
                console.log("댓글 API 활성화 여부:", shouldEnable, {
                    replyYn,
                    replyListYn: eventDetail.value.replyListYn
                });
                return shouldEnable;
            }), // replyYn과 replyListYn 모두 'Y'일 때만 활성화
            retry: 3,
            retryDelay: 1000,
        });

        // 댓글 데이터 watch
        watch(
            () => replyData.data.value,
            (newData) => {
                console.log("댓글 데이터 watch 트리거:", newData);

                if (newData && newData.page && newData.page.list) {
                    replyList.value = newData.page.list;
                    console.log("댓글 리스트 업데이트:", replyList.value);
                } else {
                    console.warn("댓글 데이터가 없습니다:", newData);
                    replyList.value = [];
                }
            },
            { immediate: true } // 즉시 실행
        );

        // 에러 모니터링
        watch(
            () => eventData.isError.value,
            (isError) => {
                if (isError) {
                    console.error("이벤트 데이터 로드 에러:", eventData.error.value);
                }
            }
        );

        watch(
            () => replyData.isError.value,
            (isError) => {
                if (isError) {
                    console.error("댓글 데이터 로드 에러:", replyData.error.value);
                }
            }
        );

        return {
            routerLink,
            replyYn,
            replyList,
            replyData,
            eventData,
            eventDetail,
            form,
            router,
            loginInfo,
            isLoggedIn,
            checkLoginAndSetName
        };
    },
    mounted() {
        console.log("컴포넌트 마운트됨");
        console.log("마운트 시 상태:", {
            eventUuid: sessionStorage.getItem('eventUuid'),
            replyYn: sessionStorage.getItem('replyYn'),
            eventDetailName: this.eventDetail.name,
            isLoading: this.eventData.isLoading.value,
            isError: this.eventData.isError.value,
            data: this.eventData.data.value,
            isLoggedIn: this.isLoggedIn,
            formName: this.form.name
        });

        // 로그인 상태 재확인
        this.checkLoginAndSetName();

        // 마운트 후 데이터가 없으면 수동으로 refetch
        nextTick(() => {
            if (!this.eventData.data.value && !this.eventData.isLoading.value) {
                console.log("수동 refetch 실행");
                this.eventData.refetch();
            }
            if (!this.replyData.data.value && !this.replyData.isLoading.value) {
                console.log("댓글 수동 refetch 실행");
                this.replyData.refetch();
            }
        });
    },
    methods: {
        /**
         * 날짜시간 포맷팅 (초 제거)
         * @param {string} dateTime - "2025-05-30 10:01:23" 형태의 날짜시간
         * @returns {string} - "2025-05-30 10:01" 형태로 변환
         */
        formatDateTime(dateTime) {
            if (!dateTime) return '';

            try {
                // "2025-05-30 10:01:23" -> "2025-05-30 10:01"
                const parts = dateTime.split(' ');
                if (parts.length === 2) {
                    const datePart = parts[0]; // "2025-05-30"
                    const timePart = parts[1]; // "10:01:23"

                    // 시간 부분에서 초 제거 (앞의 두 부분만 가져오기)
                    const timeWithoutSeconds = timePart.split(':').slice(0, 2).join(':'); // "10:01"

                    return `${datePart} ${timeWithoutSeconds}`;
                }

                return dateTime; // 형식이 예상과 다르면 원본 반환
            } catch (error) {
                console.error('날짜 포맷팅 오류:', error);
                return dateTime; // 오류 시 원본 반환
            }
        },

        /**
         * 댓글 삭제 권한 체크 (내가 쓴 댓글인지 확인)
         * @param {Object} comment - 댓글 객체
         * @returns {boolean} - 삭제 가능 여부
         */
        canDeleteComment(comment) {
            if (!this.isLoggedIn) return false;

            const currentUserId = getUserId();

            // userId 또는 userName으로 비교
            return comment.userId === currentUserId ||
                (comment.userName === this.form.name && currentUserId);
        },

        /**
         * 댓글 삭제 버튼 클릭 핸들러
         * @param {Object} comment - 삭제할 댓글 객체
         */
        handleDeleteComment(comment) {
            console.log('삭제할 댓글:', comment);

            this.selectedComment = comment;
            this.confirmDeleteShow = true;
        },

        /**
         * 댓글 삭제 확인 핸들러
         */
        async handleConfirmDelete() {
            this.confirmDeleteShow = false;

            if (!this.selectedComment) {
                console.error('삭제할 댓글이 선택되지 않았습니다.');
                return;
            }

            try {
                const deleteParams = {
                    // prefer reactive form.eventUuid (set from route param), fall back to eventDetail.uuid or sessionStorage
                    eventUuid: this.form && this.form.eventUuid ? this.form.eventUuid : (this.eventDetail && this.eventDetail.uuid) || sessionStorage.getItem('eventUuid'),
                    replySeq: this.selectedComment.seq || this.selectedComment.replySeq
                };

                console.log('댓글 삭제 요청:', deleteParams);

                const response = await deleteAxios('/hvm/v1/promotion/reply', deleteParams);

                console.log('댓글 삭제 성공:', response);
                this.alertDeleteSuccessShow = true;

                // 댓글 목록 새로고침
                this.replyData.refetch();

                // 선택된 댓글 초기화
                this.selectedComment = null;

            } catch (error) {
                console.error('댓글 삭제 중 오류 발생:', error);

                // 에러 메시지 표시 (옵션)
                alert('댓글 삭제 중 오류가 발생했습니다. 다시 시도해주세요.');

                // 선택된 댓글 초기화
                this.selectedComment = null;
            }
        },

        handleSubmitInquiry() {
            console.log('name:', this.form.name);
            console.log('rmks:', this.form.rmks);
            console.log('isLoggedIn:', this.isLoggedIn);
            console.log('replyYn:', this.replyYn);
            console.log('replyListYn:', this.eventDetail.replyListYn);

            // replyYn이 'Y'가 아니면 댓글 작성 불가
            if (this.replyYn !== 'Y') {
                alert('댓글 작성이 불가능한 이벤트입니다.');
                return;
            }

            if (this.eventDetail.replyInputYn === 'Y') {
                alert('이미 참여 완료하셨습니다.');
                return;
            }

            if (this.isLoggedIn) {
                // 로그인된 사용자의 경우
                if (!this.form.rmks) {
                    this.alertRequiredShow = true;
                    return;
                }
                this.confirmSubmitShow = true;
            } else {
                // 로그인되지 않은 사용자의 경우 로그인 필요 알림 표시        
                this.alertLoginRequiredShow = true;
            }
        },
        handleLogin() {
            this.router.push("/HFO_LO_001");
        },
        async handleConfirm() {
            this.confirmSubmitShow = false;

            try {
                // 댓글 제출 데이터 준비
                const submitData = {
                    userName: this.form.name,
                    rmks: this.form.rmks,
                    userId: this.isLoggedIn ? getUserId() : this.form.name,
                    eventUuid: this.form.eventUuid
                };

                console.log('댓글 제출 데이터:', submitData);

                const response = await postAxios('/hvm/v1/promotion/reply', submitData);

                console.log('댓글 제출 성공:', response);
                this.alertSuccessShow = true;

                // 폼 초기화 (작성자명은 로그인 상태에 따라 유지)
                this.form.rmks = '';

                // 댓글 목록 새로고침
                this.replyData.refetch();

                this.eventDetail.replyInputYn = 'Y';
            } catch (error) {
                console.error('댓글 제출 중 오류 발생:', error);
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.loading_area,
.error_area,
.no_data_area,
.no_reply_area {
    text-align: center;
    padding: 40px 20px;

    p {
        color: #666;
        margin-bottom: 20px;
    }

    .btn_retry {
        padding: 8px 16px;
        background: #007bff;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;

        &:hover {
            background: #0056b3;
        }
    }
}

.comment_writer {
    .auto_fill_info {
        margin-top: 5px;
        font-size: 12px;
        color: #888;
    }

    :deep(.auto_filled) {
        background-color: #f8f9fa;
        color: #6c757d;
    }
}

.text_input_num {
    margin-bottom: 5px;
    font-size: 12px;
    color: #666;
    text-align: right;
}

// 댓글 입력 영역 - PC 버전 정렬 개선
.comment_input_area {
    // 전체 입력 영역을 flex로 구성
    display: flex;
    flex-direction: column;
    gap: 20px;

    // 댓글 입력과 버튼을 같은 라인에 배치하기 위한 래퍼
    .input_button_wrapper {
        display: flex;
        align-items: flex-end;
        gap: 10px;

        .comment_input {
            flex: 1;
            display: flex;
            flex-direction: column;
        }

        .btn_shop_area {
            flex-shrink: 0;
            margin-top: 0;
        }
    }

    // PC 버전에서 완벽한 정렬을 위한 스타일 재정의
    @media (min-width: 1024px) {
        .btn_shop_area {
            display: flex;
            align-items: flex-end; // 하단 정렬

            .btn_type2 {
                // 높이는 유지하고 width만 50% 줄임
                height: 56px !important; // 원래 높이 유지
                min-height: 56px !important;
                width: 80px !important; // width 크게 축소
                max-width: 80px !important;
                padding: 0 8px !important; // 좌우 패딩 축소
                font-size: 12px !important;
                line-height: 56px !important; // 높이와 동일하게 설정
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                white-space: nowrap;
                border: 1px solid #ddd !important;
                border-radius: 4px !important;

                // 기본 마진/패딩 리셋
                margin: 0 !important;

                // 돋보기 관련 스타일 완전 제거
                background-image: none !important;

                &::before,
                &::after {
                    display: none !important;
                    content: none !important;
                }

                // btn_type2 색상은 contents.scss에서 자동 적용됨
            }
        }

        // 이 영역의 모든 돋보기 관련 요소 제거
        .input_button_wrapper .btn_shop_area {

            .search_icon,
            .magnify_icon,
            .btn_search,
            .search_btn,
            [class*="search"],
            [class*="magnify"],
            button[type="search"] {
                display: none !important;
            }
        }
    }

    // 모바일에서도 한 줄로 배치하되 크기 조정
    @media (max-width: 1023px) {
        .input_button_wrapper {
            gap: 8px; // 모바일에서 간격 줄임
        }

        .btn_shop_area {
            display: flex;
            align-items: flex-end; // 하단 정렬

            .btn_type2 {
                height: 48px !important; // 모바일 InputText 높이에 맞춤
                min-height: 48px !important;
                width: 60px !important; // 모바일에서 더 작게
                max-width: 60px !important;
                padding: 0 6px !important; // 좌우 패딩 더 축소
                font-size: 11px !important;
                line-height: 48px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                white-space: nowrap;
                border: 1px solid #ddd !important;
                border-radius: 4px !important;
                margin: 0 !important;

                // 모바일에서도 돋보기 제거
                background-image: none !important;

                &::before,
                &::after {
                    display: none !important;
                    content: none !important;
                }
            }
        }
    }
}

// contents.scss의 기본 스타일을 오버라이드
:deep(.comment_input_area) {
    @media (min-width: 1024px) {
        .input_type1 {
            height: 56px !important; // InputText는 기존 크기 유지
            line-height: 54px !important;
            padding: 0 16px !important;
            font-size: 16px !important;
        }
    }

    @media (max-width: 1023px) {
        .input_type1 {
            height: 48px !important; // 모바일 InputText 높이
            line-height: 46px !important;
            padding: 0 12px !important;
            font-size: 14px !important;
        }
    }
}

// 댓글 입력 영역에서만 돋보기 제거 (목록보기 버튼은 영향받지 않음)
.comment_input_area {

    // 이 영역 내의 모든 돋보기 관련 요소 제거
    :deep(.search_icon),
    :deep(.magnify_icon),
    :deep(.btn_search),
    :deep(.search_btn),
    :deep([class*="search"]),
    :deep([class*="magnify"]),
    :deep(button[type="search"]) {
        display: none !important;
    }
}

.comment_list {
    li {
        padding: 15px 0;
        border-bottom: 1px solid #eee;

        &:last-child {
            border-bottom: none;
        }
    }

    .comment_header {
        margin-bottom: 8px;

        .comment_writer_info {
            display: flex;
            flex-direction: column;
            align-items: flex-start;

            .comment_writer2 {
                font-weight: bold;
                color: #333;
                max-width: 150px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                line-height: 1.4;
            }

            .comment_date {
                font-size: 13px;
                color: #888;
                line-height: 1.3;
            }
        }
    }

    .comment_content {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 15px;

        .comment_txt {
            flex: 1;
            color: #555;
            line-height: 1.5;
        }

        .btn_delete {
            flex-shrink: 0;
            padding: 4px 8px;
            font-size: 11px;
            background: #dc3545;
            color: white;
            border: none;
            border-radius: 3px;
            cursor: pointer;
            transition: background-color 0.2s;
            min-width: 40px;
            line-height: 1.4;

            &:hover {
                background: #c82333;
            }
        }
    }

    @media (max-width: 768px) {
        .comment_header {
            .comment_writer_info {
                .comment_writer2 {
                    max-width: 120px;
                }

                .comment_date {
                    font-size: 12px;
                }
            }
        }

        .comment_content {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;

            .btn_delete {
                align-self: flex-end;
                margin-top: 5px;
            }
        }
    }
}

.btn_layer_area {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-top: 20px;

    .btn_type1,
    .btn_type2 {
        &.small {
            padding: 8px 16px;
            font-size: 12px;
            min-width: 60px;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }
}

.participated_area {
    text-align: center;
    padding: 40px 0 20px 0;
}

.participated_txt {
    font-size: 18px;
    color: #007bff;
    font-weight: bold;
}
</style>
